import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgForage } from 'ngforage';
import { Subject } from 'rxjs';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import Company from 'src/app/model/company/company';
import Contact from 'src/app/model/Contact/contact';
import { SnackService } from 'src/app/services/snack.service';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import {
  ALL_BOARD_TYPE_SEARCH_PARAMETERS,
  BoardTypeSearchParameter
} from './helper/board-type-search-parameter';
import { SearchSubProjectsInput } from './helper/search-subprojects-input';
import { SearchSubProjectsInputHelper } from './helper/search-subprojects-input-helper';
import { SelectableSearchParameter } from './helper/selectable-search-parameter';
import {
  SubProjectTypeSearchParameter,
  SUB_PROJECT_TYPE_SEARCH_PARAMETER_CHOICES
} from './helper/sub-project-type-search-parameter';

@Component({
  selector: 'search-subprojects-input',
  templateUrl: './search-subprojects-input.component.html',
  styleUrls: ['./search-subprojects-input.component.scss']
})
export class SearchSubProjectsInputComponent extends SubscriptionBasedComponent
  implements OnInit, OnDestroy {
  searchInput: SearchSubProjectsInput;

  contactChoices: Contact[];
  subProjectTypeChoices: SelectableSearchParameter<
    SubProjectTypeSearchParameter
  >[] = SUB_PROJECT_TYPE_SEARCH_PARAMETER_CHOICES;
  boardTypeSearchChoices: SelectableSearchParameter<BoardTypeSearchParameter>[];

  storeInputSubject = new Subject<any>();

  constructor(
    private ngForage: NgForage,
    private router: Router,
    private snackService: SnackService
  ) {
    super();
  }

  ngOnInit(): void {
    this.doInitialize();

    this.addSubscription(
      this.storeInputSubject,
      () =>
        SearchSubProjectsInputHelper.storeInput(
          this.ngForage,
          this.searchInput
        ),
      () => GeneralHelper.noop,
      500
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.storeInputSubject != null) {
      this.storeInputSubject.complete();
      this.storeInputSubject = null;
    }
  }

  companySelected(company: Company): void {
    this.searchInput.company = company;
    this.searchInput.contact = null;
    this.contactChoices = [...company.contacts];
    this.storeInputSubject.next();
  }

  resetProjectId(): void {
    this.searchInput.projectId = null;
    this.storeInputSubject.next();
  }

  resetCompany(): void {
    this.searchInput.company = new Company();
    this.searchInput.contact = null;
    this.contactChoices = null;
    this.storeInputSubject.next();
  }

  resetContact(): void {
    this.searchInput.contact = null;
    this.storeInputSubject.next();
  }

  resetStreet(): void {
    this.searchInput.street = null;
    this.storeInputSubject.next();
  }

  resetNumber(): void {
    this.searchInput.number = null;
    this.storeInputSubject.next();
  }

  resetCity(): void {
    this.searchInput.city = null;
    this.storeInputSubject.next();
  }

  resetPostalCode(): void {
    this.searchInput.postalCode = null;
    this.storeInputSubject.next();
  }

  resetSubProjectTypes(): void {
    this.searchInput.subProjectTypes = [];
    this.enforceBoardTypeConsistency();
    this.storeInputSubject.next();
  }

  resetBoardTypes(): void {
    this.searchInput.boardTypeSearchParameters = [];
    this.storeInputSubject.next();
  }

  private async doInitialize(): Promise<void> {
    const searchInput = await SearchSubProjectsInputHelper.initializeInput(
      this.ngForage
    );
    if (
      searchInput.company != null &&
      searchInput.company.id != null &&
      CollectionHelper.isPopulated(searchInput.company.contacts)
    ) {
      this.contactChoices = [...searchInput.company.contacts];
    }

    this.boardTypeSearchChoices = SearchSubProjectsInputHelper.getValidBoardTypeChoicesForSubProjectTypes(
      searchInput.subProjectTypes
    );

    this.searchInput = searchInput;
  }

  subProjectTypeChanged(): void {
    this.enforceBoardTypeConsistency();
    this.storeInputSubject.next();
  }

  private enforceBoardTypeConsistency() {
    this.boardTypeSearchChoices = SearchSubProjectsInputHelper.getValidBoardTypeChoicesForSubProjectTypes(
      this.searchInput.subProjectTypes
    );
    SearchSubProjectsInputHelper.restrictSelectedBoardTypeChoices(
      this.searchInput.boardTypeSearchParameters,
      this.searchInput.subProjectTypes
    );
  }

  resetInput(): void {
    this.searchInput = SearchSubProjectsInputHelper.resetInput(this.ngForage);
    this.boardTypeSearchChoices = ALL_BOARD_TYPE_SEARCH_PARAMETERS;
    this.contactChoices = null;
  }

  async submitInput(): Promise<void> {
    if (!SearchSubProjectsInputHelper.inputIsValid(this.searchInput)) {
      this.snackService.showError(
        'Gelieve minstens één zoekparameter in te vullen'
      );
      return;
    }

    await SearchSubProjectsInputHelper.storeInputAsync(
      this.ngForage,
      this.searchInput
    );
    this.router.navigate(['zoek-jobs-resultaat']);
  }

  compareEntities(o1: any, o2: any): boolean {
    return GeneralHelper.compareEntities(o1, o2);
  }
}
